import { Box, Button, Stack, styled, Typography } from "~/common/mui";
import type { ReactNode } from "react";
import {
  IMG_CONTRACTS_CALENDAR,
  IMG_CONTRACTS_CART,
  IMG_CONTRACTS_SPEED,
  IMG_CONTRACTS_USERS
} from "~/utils/url/staticResources";
import { NEW_CONTRACT_PAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";

const ItemTextContainer = styled("div")({
  "& > p": { margin: 0 },
});

interface EmptyContractsScreenProps {
  className?: string;
}

const BenefitsItem = ({ icon, title, text }: { icon: ReactNode; title: string; text: string }) => (
  <Stack alignItems="center" gap={4} flex={1} sx={{ textAlign: "center" }}>
    {icon}
    <ItemTextContainer>
      <Typography variant="bodyMedium" fontWeight="bold">{title}</Typography>
      <p>{text}</p>
    </ItemTextContainer>
  </Stack>
);

const EmptyContractsScreen = ({ className }: EmptyContractsScreenProps) => {
  const { t } = useTranslation();
  return (
    <Stack direction="column" flex={1} alignItems="center" gap={4}>
      <Typography variant="displaySmall" sx={{ mt: 4 }}>{t("contractsList.empty")}</Typography>
      <Button
        href={NEW_CONTRACT_PAGE}
        sx={{ width: "fit-content" }}
        startIcon={<AddIcon  />}
        clta-main-page-element-marker="request-new-contract-link"
      >
        {t("newContract.title")}
      </Button>
      <Stack sx={(theme) => ({ width: "100%", p: theme.spacing(4, 3), border: 1, borderColor: "divider", borderRadius: theme.spacing(0.5) })}>
        <Typography variant="headlineSmall" sx={{ mb: 4 }}>{t("contractsList.benefitsTitle")}</Typography>
        <Stack direction="row" gap={3} sx={{ bgcolor: "surface.main", p: 3, "& i": { color: "primary.main" } }}>
          <BenefitsItem
            icon={<Box component="img" src={IMG_CONTRACTS_CART} sx={{ width: 64, aspectRatio: "1/1" }} />}
            title={t("contractsList.benefits.cart.title")}
            text={t("contractsList.benefits.cart.text")}
          />
          <BenefitsItem
            icon={<Box component="img" src={IMG_CONTRACTS_CALENDAR} sx={{ width: 64, aspectRatio: "1/1" }} />}
            title={t("contractsList.benefits.calendar.title")}
            text={t("contractsList.benefits.calendar.text")}
          />
          <BenefitsItem
            icon={<Box component="img" src={IMG_CONTRACTS_SPEED} sx={{ width: 64, aspectRatio: "1/1" }} />}
            title={t("contractsList.benefits.speed.title")}
            text={t("contractsList.benefits.speed.text")}
          />
          <BenefitsItem
            icon={<Box component="img" src={IMG_CONTRACTS_USERS} sx={{ width: 64, aspectRatio: "1/1" }} />}
            title={t("contractsList.benefits.users.title")}
            text={t("contractsList.benefits.users.text")}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default EmptyContractsScreen;
